<script>
    import {PATH_PREFIX} from '../../apiUrls.js';

    import Stepper from '../Stepper/Stepper.svelte';
    import StepperNavigator from '../Stepper/StepperNavigator.svelte/';
    import {onMount, afterUpdate} from 'svelte';
    import {
        relatedActivities,
        specification as specificationStore,
        tool as toolStore,
        tools as toolListStore,
    } from './store.js';
    import Modal from '../Modal.svelte';
    import {validateForm} from '../Form/utils.js';
    import {get} from '../../utils.js';
    import DuplicateCheckTextInput from '../DuplicateCheckTextInput.svelte';
    import TextInput from '../TextInput.svelte';
    import ImageInput from './ImageInput.svelte';
    import SpecificationForm from './SpecificationForm.svelte';
    import ToolDetail from './ToolDetail.svelte';
    import TagSelect from '../Tag/TagSelect.svelte';
    // import RelatedActivities from './RelatedActivities.svelte';
    import Errors from '../Errors.svelte';
    import {submit} from '../../utils.js';
    import yup, {object, string, number} from 'yup';
    import {sgettext} from '../../i18n.js';

    export let title = sgettext('$$ADD_TOOL$$');
    export let id = 'globalToolForm';
    export let actionUrl = `${PATH_PREFIX}/sw/api/elements_database/globaltools/`;
    export let duplicateCheckApi = `${PATH_PREFIX}/sw/api/elements_database/globaltools/check_duplicate/`;
    export let specificationFormUrl = `${PATH_PREFIX}/sw/api/elements_database/globaltools/`;
    // export let relatedActivitiesApi = `${PATH_PREFIX}/sw/api/affected-activities/`;

    export let editId = 0;
    export let currentStep = 1;

    $: title = $toolStore.id > 0 ? sgettext('$$EDIT_TOOL$$') : sgettext('$$ADD_TOOL$$');

    let getTagsApi = `${PATH_PREFIX}/sw/api/elements_database/element_tags/`;
    let isValid = true;
    let showSpecificationForm = false;
    let toolNameBeforeEdit = '';
    let duplicateCheckHardFail = false;
    let addAnother = false;

    const FILE_SIZE = 100 * 1024 * 1024;
    const SUPPORTED_FORMATS = [
        "image/jpg",
        "image/jpeg",
        "image/gif",
        "image/png",
        "video/mp4"
    ];

    let schema = object().shape({
        name: string()
                .required(sgettext("$$REQUIRED_FIELD$$"))
                .test(
                    sgettext("$$DUPLICATE_1$$"),
                    sgettext("$$TOOL_NAME_ALREADY_EXISTS$$"),
                    _ => !duplicateCheckHardFail,
                ),
        image: yup.mixed()
                .test(
                        sgettext("$$FILESIZE$$"),
                        sgettext("$$FILE_LARGE$$"),
                        value => {
                            if (value && !value.id) {
                                return value && value.size <= FILE_SIZE
                            }
                            else {
                                return true;
                            }
                        }
                )
                .test(
                        sgettext("$$FILEFORMAT$$"),
                        sgettext("$$UNSUPPORTED_FORMAT$$"),
                        value => {
                            if (value && !value.id) {
                                return value && SUPPORTED_FORMATS.includes(value.type);
                            }
                            else {
                                return true;
                            }
                        }
                )
                .required(sgettext("$$IMAGE_REQUIRED$$"))
    });

    function handleImageRemoval() {
        $toolStore.image = null;
    }

    async function handleImageChange(image) {
        image.url = URL.createObjectURL(image);
        $toolStore.image = image;
    }

    function handleTagChange() {
        $toolStore.tags = $toolStore.tags.map(t => t.name? t.name : t);
    }

    let errors = null;
    let sending = false;

    export let tool = {
        name: '',
        specifications: [{
            id: '',
            description: '',
            image: null,
            tags: '',
            deleted: false
        }],
        specification_images: [],
    };

    let steps = [
        {
            order: 1,
            label: sgettext('$$TOOL_DETAIL$$'),
            done: false,
            isValid: true,
            fieldIsValid: {
                name: {
                    isValid: true,
                    errors: ''
                },
                description: {
                    isValid: true,
                    errors: ''
                },
                tags: {
                    isValid: true,
                    errors: ''
                },
                image: {
                    isValid: true,
                    errors: ''
                },
            }
        },
        {
            order: 2,
            label: sgettext('$$TOOL_SPECIFICATIONS$$'),
            done: false,
            isValid: true,
            errors: [],
        },
        {
            order: 3,
            label: sgettext('$$REVIEW$$'),
            done: false,
            isValid: false
        },
    ];

    let finalStep = 3;

    toolStore.set(tool);

    let hidePopup = true;
    let data = [];

    $: if (editId) {
        getEditItem();
    }

    $: {
        // ensure that a control can only be added once
        $toolStore.specifications = [...new Set($toolStore.specifications)];
    }

    async function handleFormValidation() {
        steps[1].errors = '';

        try {
            if (currentStep === 2) {
                let specificationsValid = true;
                let hasAtLeastOneSpecification = false;

                for (let spec of $toolStore.specifications) {
                    if (spec.deleted) {
                        continue;
                    }
                    else {
                        specificationsValid = specificationsValid && spec.description !== '';
                        hasAtLeastOneSpecification = true;
                    }

                    for (let spec2 of $toolStore.specifications) {
                        if (spec.id === spec2.id || spec2.deleted) {
                            spec.isValid = true;
                            spec.errors = '';
                            continue;
                        }

                        if (spec2.description === spec.description) {
                            spec.isValid = false;
                            spec.errors = sgettext('$$SPECIFICATION_NAME_ALREADY_EXISTS$$');
                            break;
                        }
                        else if (spec2.id !== spec.id) {
                            spec.isValid = true;
                            spec.errors = '';
                        }
                    }

                    if (!spec.deleted && !spec.isValid) {
                        specificationsValid = false;
                    }
                }

                steps[1].isValid = specificationsValid && hasAtLeastOneSpecification;

                if (!hasAtLeastOneSpecification) {
                    steps[1].errors = sgettext('$$LEAST_ONE_SPECIFICATION_REQUIRED$$');
                }

                if (!steps[1].isValid) {
                    throw Error(sgettext("$$SPEC_INCOMPLETE$$"));
                }
            } else if (currentStep === 1) {
                await validateForm(schema, $toolStore);
                isValid = true;
                getRelatedActivities();
            }

            updateStepStatus(isValid);
            if (currentStep === finalStep) {
                handleSave();
            } else {
                currentStep = currentStep + 1;
            }

            if (currentStep > finalStep) {
                currentStep = finalStep;
            }
        } catch (err) {
            isValid = false;
            updateStepStatus(isValid);
            if (err.inner) {
                err.inner.forEach(er => {
                    steps[currentStep - 1].fieldIsValid[er.path].isValid = false;
                    steps[currentStep - 1].fieldIsValid[er.path].errors = er.message;
                });
            }
            else {
                console.log(err);
            }

        }
    }

    function updateStepStatus(valid) {
        const current = steps.filter(s => s.order === currentStep);
        if (current.length) {
            current[0].isValid = valid;
        }
    }

    function handleAddSpecification() {
        $toolStore.specifications = [...new Set($toolStore.specifications), {id: -1 * ($toolStore.specifications.length + 1), description: '', image: '', tags: ''}];
        setTimeout(() => jQuery(`input.text-input:text:visible:last`).focus(), 500);
    }

    function reset() {
        Object.keys(steps[0].fieldIsValid).forEach(k => {
            steps[0].fieldIsValid[k].isValid = true;
            steps[0].fieldIsValid[k].errors = '';
        });

        steps[1].errors = '';
        duplicateCheckHardFail = false;
    }

    function handleClose() {
        let tool = {
            name: '',
            description: '',
            image: null,
            tags: [],
            specifications: [{
                id: '',
                description: '',
                image: null,
                tags: '',
                deleted: false
            }],
            specification_images: []
        };

        let specification = {
            description: '',
        };

        toolStore.set(tool);
        specificationStore.set(specification);
        hidePopup = true;
        currentStep = 1;
        editId = 0;
        isValid = true;
        sending = false;
        showSpecificationForm = false;
        errors = '';
        addAnother = false;
    }

    function getToolDetail() {
        const detail = {
            name: $toolStore.name,
            description: $toolStore.description,
            image: $toolStore.image,
            tags: $toolStore.tags,
            specifications: $toolStore.specifications
        };

        return detail;
    }

    function handleDuplicate(event) {
        isValid = true;
        duplicateCheckHardFail = false;
        if (event.detail.length) {
            if (event.detail[0].text !== toolNameBeforeEdit && event.detail[0].similarity === 100) {
                isValid = false;
                duplicateCheckHardFail = true;
            }
        }
    }

    async function getEditItem() {
        if (editId) {
            const response = await get(`${actionUrl}${editId}/`);
            const json = await response.json();

            json.specification_images = [];

            if (json) {
                toolNameBeforeEdit = json.name;
                // json.specifications = json.specifications.filter(s => s);
                toolStore.set(json);
            }

            return json;
        }
    }

    async function getRelatedActivities() {
        if (editId > 0) {
            // const response = await get(`${relatedActivitiesApi}${editId}/`);
            // const json = await response.json();
            //
            // relatedActivities.set(json);
        }
    }

    onMount(async () => {

        jQuery(`#${id}`).on('show.bs.modal', function () {
            reset();
        });

        jQuery(`#${id}`).on('shown.bs.modal', function () {
            jQuery(`#name`).trigger('focus');
        });


        jQuery(`#${id}`).on('hidden.bs.modal', function () {
            handleClose();
        });
    });

    async function handleSave() {
        sending = true;
        let method = 'POST';
        let url = actionUrl;
        if (editId) {
            method = 'PUT';
            url = `${actionUrl}${editId}/`
        }

        let resp = await submit(url, method, getPayload(), null, true);
        if (resp.ok) {
            let newTool = await resp.json();
            let toolList = [newTool, ...$toolListStore.filter(t => t.id !== $toolStore.id)];
            toolListStore.set(toolList);
            sending = false;

            let keepModalOpen = addAnother;
            handleClose();
            reset();
            if (!keepModalOpen) {
                jQuery(`#${id}`).modal('hide');
            }
        } else {
            errors = await resp.json();
            sending = false;
        }

    }

    function getPayload() {

        const formData = new FormData();
        if ($toolStore.id)
            formData.append('id', $toolStore.id);
        formData.append('name', $toolStore.name);
        formData.append('description', $toolStore.description);

        formData.append('tags', $toolStore.tags.map((t) => t.hasOwnProperty('name') ? t.name : t));
        let specifications = $toolStore.specifications.map(t => t);
        for (const s of specifications) {
            delete s.errors;
            delete s.isValid;
        }
        formData.append('specifications', JSON.stringify(specifications));

        for (let specImage of $toolStore.specification_images) {
            let spec = $toolStore.specifications.filter(s => s.id === specImage.id).pop();
            if (spec && spec.image) {
                formData.append(`specification_image_${specImage.id}`, specImage);
            }
        }

        if ($toolStore.image.id) {
            formData.append('image', JSON.stringify($toolStore.image));
        }
        else {
            formData.append('image', $toolStore.image);
        }

        return formData;
    }

    function blockSubmit(e) {
        // prevent regular form submission by pressing ENTER key.
        e.preventDefault();
    }

</script>

<style>
    .footer {
        width: 100%;
    }
</style>

<Modal
        {id}
        {title}
        onCancel={handleClose}
        onSave={handleSave}
        backdrop={'static'}
        large={true}>
    <div slot="body">
        <form on:submit={blockSubmit}>
            <Stepper {steps} bind:currentStep/>

            {#if currentStep === 1}
                <div class="form-group">
                    <label for="image">{sgettext('$$TOOL_IMAGE_1$$')}</label>
                    <ImageInput
                        bind:media={$toolStore.image}
                        width=""
                        height="200px"
                        onRemove={handleImageRemoval}
                        onChange={handleImageChange}
                        bind:isValid={steps[0].fieldIsValid.image.isValid}
                        bind:errors={steps[0].fieldIsValid.image.errors}/>
                </div>
                <div class="form-group">
                    <DuplicateCheckTextInput
                            id="name"
                            required="true"
                            apiurl={duplicateCheckApi}
                            label={sgettext("$$TOOL_NAME_1$$")}
                            message={sgettext("$$SIMILAR_TOOL_FOUND$$")}
                            placeholder={sgettext("$$EG_OPEN_END_SPANNERS$$")}
                            bind:value={$toolStore.name}
                            bind:isValid={steps[0].fieldIsValid.name.isValid}
                            bind:errors={steps[0].fieldIsValid.name.errors}
                            on:check={handleDuplicate}
                            multiplyPercentage="false"
                            {schema}/>
                </div>
                <div class="form-group">
                    <TextInput
                        id="description"
                        required="true"
                        label={sgettext("$$TOOL_DESCRIPTION$$")}
                        multiline={true}
                        bind:value={$toolStore.description}
                        bind:isValid={steps[0].fieldIsValid.description.isValid}
                        bind:errors={steps[0].fieldIsValid.description.errors}/>
                </div>
                <div class="form-group">
                    <label for="tags">{sgettext('$$TOOL_TAGS$$')}</label>
                    <TagSelect
                        id="toolTags"
                        bind:values={$toolStore.tags}
                        extraStyle="height: 50px;"
                        filterKey="q"
                        optionsApiUrl={getTagsApi}
                        on:changed={handleTagChange}
                        allowNewOption={true}
                        maxSelection={10}
                        bind:isValid={steps[0].fieldIsValid.tags.isValid}
                        bind:errors={steps[0].fieldIsValid.tags.errors} />
                </div>

            {:else if currentStep === 2}
                <Errors errors={steps[1].errors}/>

                {#each $toolStore.specifications as specification}
                    <SpecificationForm
                        {specification}
                        actionUrl={specificationFormUrl}
                        bind:isValid={steps[1].isValid}
                        />
                {/each}
                <div class="text-right">
                    <button type="button" class="btn btn-link text-primary" on:click={handleAddSpecification}>
                        <strong><i class="fas fa-plus"></i> {sgettext('$$ADD_SPECIFICATION$$')}</strong>
                    </button>
                </div>
            {:else}
                <div class="form-group">
                    <ToolDetail tool={getToolDetail()}/>
                    {#if $relatedActivities.hasOwnProperty('results') && $relatedActivities.results.length}
                    <!--                        <RelatedActivities data={$relatedActivities}/>-->
                    {/if}
                </div>
            {/if}
            {#if errors}
                <Errors {errors}/>
            {/if}
        </form>
    </div>

    <div slot="footer" class="footer">
        <StepperNavigator
                bind:currentStep
                bind:addAnother
                enableSaveAndAddAnother={true}
                {isValid}
                {sending}
                back={() => errors = null}
                next={handleFormValidation} />
    </div>
</Modal>
